<script lang="ts" setup>
import { useWindowSize } from '@vueuse/core'
import type { Ref } from 'vue'
import { computed, inject, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router/composables'
import { THEME_SIZE_CLASSES } from '@/constants'
import type { THEME_BACKGROUNDS } from '@/constants'
import { clamp, sleep } from '@/utils'

// TODO: Move types.d.ts
type BACKGROUND = typeof THEME_BACKGROUNDS[number]
type SIZE = keyof typeof THEME_SIZE_CLASSES

const props = defineProps<{
  value: number
  content: string
  title: string
  progress: { unit: number; index: number }
}>()

const emit = defineEmits<{
  (event: 'input', value: number): void
  (event: 'menu'): void
  (event: 'change', index: number, pos: number): void
  (event: 'prevChapter'): void
  (event: 'nextChapter'): void
}>()

const theme = inject<Ref<{
  background: BACKGROUND
  size: SIZE
}>>('theme', ref({ background: '#FFF', size: 'md' } as any))
const themeSizeClasses = computed(() => {
  return THEME_SIZE_CLASSES[theme!.value.size]
})

const paragraphs = computed(() => {
  if (!props.content)
    return []
  return props.content.split(/[\n<br>]/).filter(p => Boolean(p.trim()))
})

const articleRef = ref<HTMLElement | null>(null)
const { width: windowWidth } = useWindowSize()
const scrollWidth = computed(() => {
  return articleRef.value?.scrollWidth ?? 0
})
const length = computed(() => {
  return Math.round(scrollWidth.value / windowWidth.value)
})

let touchStartX = 0
let touchMoveX = 0
// let touchEndX = 0 TODO: need?
const translateX = ref(0)
watch(() => props.value, (val) => {
  translateX.value = -windowWidth.value * val
})
function onTouchStart(event: TouchEvent) {
  touchStartX = event.touches[0].clientX
}
function onTouchMove(event: TouchEvent) {
  touchMoveX = event.touches[0].clientX
  translateX.value = props.value * -windowWidth.value + touchMoveX - touchStartX
}
function onTouchEnd(event: TouchEvent) {
  // touchEndX = event.changedTouches[0].clientX
  // Note: is click
  if (touchMoveX === 0) {
    if (touchStartX < windowWidth.value / 3)
      onPrev()
    else if (touchStartX < windowWidth.value / 3 * 2)
      emit('menu')
    else
      onNext()
  }
  else if (translateX.value > props.value * -windowWidth.value) {
    onPrev()
  }
  else {
    onNext()
  }

  // Note: reset
  touchStartX = 0
  touchMoveX = 0

  event.stopPropagation()
  event.preventDefault()
}

function onPrev() {
  if (props.value === 0) {
    translateX.value = 0
    emit('prevChapter')
    return
  }
  emit('input', props.value - 1)
  emit('change', props.value, -1)
}
function onNext() {
  if (props.value === length.value - 1) {
    translateX.value = -props.value * windowWidth.value
    emit('nextChapter')
    return
  }
  emit('input', props.value + 1)
  emit('change', props.value, 1)
}

const totalProgress = computed(() => {
  const chapter = props.progress.unit * (props.progress.index - 1) * 100
  const current = props.value / length.value * props.progress.unit * 100
  return clamp(chapter + current, 0, 100).toFixed(2)
})

defineExpose({
  length,
  totalProgress,
  change(val: number) {
    emit('input', clamp(val, 0, length.value - 1))
  },
})

const route = useRoute()
const invisible = ref(true)
onMounted(async () => {
  // Note: first render, sleep `200ms` for disabled animate
  if (route.query.swiperIndex) {
    await sleep(200)
    invisible.value = false
    return
  }
  invisible.value = false
})
</script>

<template>
  <div
    :class="{ invisible }"
    @touchstart="onTouchStart" @touchmove="onTouchMove" @touchend="onTouchEnd"
  >
    <article
      ref="articleRef" class="h-full transition-all duration-200 ease-linear"
      :style="{ transform: `translateX(${translateX}px)` }"
    >
      <p class="px-5" :class="themeSizeClasses.title">
        {{ title }}
      </p>
      <div class="px-5" :class="themeSizeClasses.content">
        <p v-for="(p, idx) in paragraphs" :key="idx" :class="themeSizeClasses.spacing">
          {{ p }}
        </p>
      </div>
    </article>
    <p class="px-5 flex items-center justify-between text-sm text-#999">
      <span>本书进度 {{ totalProgress }}%</span>
      <span>{{ value + 1 }}/{{ length }}</span>
    </p>
  </div>
</template>

<style scoped>
article {
  height: 100%;
  columns: 100vw 1;
  column-gap: 0;
  word-break: normal;
  word-wrap: break-word;
  column-fill: auto;
}
</style>
